import React from 'react';
import { createRoot } from 'react-dom/client';
import Container from './components/Container';
import { getWidgetConfig, saveWidgetConfig, loadWidgetConfig } from './services/tenant';
import './styles/base.css';



/**
 * TalkFlow Widget Class
 */
class TalkFlowWidget {
  constructor(config = {}) {
    // Tải cấu hình từ localStorage nếu có
    const savedConfig = loadWidgetConfig() || {};

    // Merge cấu hình từ các nguồn khác nhau, ưu tiên từ cao đến thấp:
    // 1. config từ tham số constructor
    // 2. config từ localStorage
    // 3. config từ data attributes của script tag hoặc các nguồn khác
    this.config = {
      ...getWidgetConfig(),
      ...savedConfig,
      ...config
    };

    this.widgetRoot = null;
    this.containerDiv = null;
    this.floatingButton = null;
    this.isInitialized = false;
    this.root = null;
    this.floatingButtonRoot = null;

    // Initialize the widget
    this.init();
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    // Create container div
    this.containerDiv = document.createElement('div');
    this.containerDiv.className = 'talkflow-widget';

    if (this.config.position === 'left') {
      this.containerDiv.classList.add('left-position');
    }

    // Add to body
    document.body.appendChild(this.containerDiv);

    // Create React root
    this.root = createRoot(this.containerDiv);

    // Create floating button
    this.createFloatingButton();

    // Render React component
    this.render();

    // Initially hide the widget and show the floating button
    this.hide();

    this.isInitialized = true;

    console.log('TalkFlow initialized with config:', this.config);
  }

  createFloatingButton() {
    // Create floating button element
    this.floatingButton = document.createElement('div');
    this.floatingButton.className = 'talkflow-widget-button';

    if (this.config.position === 'left') {
      this.floatingButton.style.right = 'auto';
      this.floatingButton.style.left = '20px';
    }

    // Add Font Awesome AI robot icon
    this.floatingButton.innerHTML = `
      <i class="fa-solid fa-robot"></i>
    `;

    // Add click event to show widget
    this.floatingButton.addEventListener('click', () => {
      this.show();
    });

    // Add to body
    document.body.appendChild(this.floatingButton);
  }

  render() {
    if (this.root) {
      this.root.render(
        <Container
          config={this.config}
          onClose={() => this.hide()}
        />
      );
    }
  }

  updateConfig(newConfig) {
    this.config = {
      ...this.config,
      ...newConfig
    };

    // Update position class
    if (newConfig.position) {
      this.containerDiv.classList.remove('left-position');
      if (newConfig.position === 'left') {
        this.containerDiv.classList.add('left-position');

        if (this.floatingButton) {
          this.floatingButton.style.right = 'auto';
          this.floatingButton.style.left = '20px';
        }
      } else {
        if (this.floatingButton) {
          this.floatingButton.style.left = 'auto';
          this.floatingButton.style.right = '20px';
        }
      }
    }

    // Lưu cấu hình mới vào localStorage
    saveWidgetConfig(this.config);

    // Re-render with new config
    this.render();

    console.log('TalkFlow config updated:', this.config);
  }

  show() {
    if (this.containerDiv) {
      this.containerDiv.style.display = 'flex';
    }

    if (this.floatingButton) {
      this.floatingButton.style.display = 'none';
    }
  }

  hide() {
    if (this.containerDiv) {
      this.containerDiv.style.display = 'none';
    }

    if (this.floatingButton) {
      this.floatingButton.style.display = 'flex';
    }
  }

  toggle() {
    if (this.containerDiv) {
      if (this.containerDiv.style.display === 'none') {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  destroy() {
    if (this.root && this.containerDiv) {
      // Unmount with React 18 API
      this.root.unmount();
      document.body.removeChild(this.containerDiv);
      this.containerDiv = null;
      this.root = null;

      if (this.floatingButton) {
        document.body.removeChild(this.floatingButton);
        this.floatingButton = null;
      }

      this.isInitialized = false;
    }
  }
}

// Tạo instance toàn cục
let talkflowInstance = null;

// Gán TalkFlowWidget class vào window
window.TalkFlowWidget = TalkFlowWidget;

// Gán trực tiếp vào window object
window.TalkFlow = {
  show: function () {
    if (!talkflowInstance) {
      talkflowInstance = new TalkFlowWidget();
    }
    talkflowInstance.show();
  },

  hide: function () {
    if (!talkflowInstance) {
      talkflowInstance = new TalkFlowWidget();
    }
    talkflowInstance.hide();
  },

  toggle: function () {
    if (!talkflowInstance) {
      talkflowInstance = new TalkFlowWidget();
    }
    talkflowInstance.toggle();
  },

  updateConfig: function (config) {
    if (!talkflowInstance) {
      talkflowInstance = new TalkFlowWidget();
    }
    talkflowInstance.updateConfig(config);
  },

  getInstance: function () {
    if (!talkflowInstance) {
      talkflowInstance = new TalkFlowWidget();
    }
    return talkflowInstance;
  },

  resetConfig: function () {
    try {
      localStorage.removeItem('talkflow_config');
      if (talkflowInstance) {
        talkflowInstance.destroy();
        talkflowInstance = null;
      }
      return this.getInstance();
    } catch (error) {
      console.error('Error resetting config:', error);
      return null;
    }
  }
};

// Initialize on DOM load
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Auto-initialize to ensure TalkFlow is available
      console.log('TalkFlow loaded and ready - creating initial instance');
      if (!talkflowInstance) {
        talkflowInstance = new TalkFlowWidget();
      }
    });
  } else {
    // DOM already loaded
    console.log('TalkFlow loaded and ready - creating initial instance');
    if (!talkflowInstance) {
      talkflowInstance = new TalkFlowWidget();
    }
  }
}

export default window.TalkFlow;
